import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { keys, useChildState, useDeselectChild } from "../api/hooks.ts";
import {
  ChildAvatar,
  Icon,
  ProgressBar,
  SoundToggle,
  Spinner,
  StatPill,
} from "../components/ui.tsx";
import { copy } from "../copy/index.ts";
import { ErrorScreen } from "../screens/ErrorScreen.tsx";
import { flush } from "../sync/outbox.ts";
import styles from "./ChildShell.module.css";

const NAV = [
  { to: "/ban-do", label: copy.nav.home, icon: "island" },
  { to: "/luyen-tap", label: copy.nav.practice, icon: "pencil" },
  { to: "/thanh-tich", label: copy.nav.achievements, icon: "medal" },
  { to: "/xep-hang", label: copy.nav.leaderboard, icon: "trophy" },
  { to: "/cua-hang", label: copy.nav.shop, icon: "bag" },
];

/** Child-mode layout (SPEC §8.1): header with stats, side nav ≥ 900px, bottom nav on phones. */
export function ChildShell() {
  const state = useChildState();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const deselect = useDeselectChild();
  const childId = state.data?.child.id;
  const { pathname } = useLocation();

  // a new page starts at its top (the router keeps the old scroll position otherwise)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // deliver sessions finished while offline
  useEffect(() => {
    if (!childId) return;
    void flush(childId).then((r) => {
      if (r) qc.setQueryData(keys.state, r.state);
    });
  }, [childId, qc]);

  if (state.isPending) return <Spinner />;
  if (state.isError || !state.data) return <ErrorScreen onRetry={() => void state.refetch()} />;
  const { child, stats } = state.data;

  return (
    <div className={styles.shell}>
      <aside className={styles.side}>
        <div className={styles.logo}>
          <Icon name="bee" size={44} />
          <span>{copy.app.name}</span>
        </div>
        <nav className={styles.sideNav} aria-label="Điều hướng">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `${styles.sideLink} ${isActive ? styles.active : ""}`}
            >
              <Icon name={n.icon} size={30} />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <header className={styles.top}>
        <button
          type="button"
          className={styles.who}
          onClick={() => deselect.mutate(undefined, { onSuccess: () => void navigate("/chon-be") })}
          aria-label={copy.nav.switchChild}
        >
          <ChildAvatar avatar={child.avatar} size={52} />
          <span className={styles.whoText}>
            <strong>{child.displayName}</strong>
            <span className={styles.level}>
              Cấp {stats.level} · {stats.title}
            </span>
            <ProgressBar value={stats.xpInLevel} max={stats.xpForNext || 1} tone="honey" />
          </span>
        </button>
        <div className={styles.stats}>
          <StatPill icon="star" value={stats.starsTotal} label="Tổng số sao" />
          <StatPill icon="coin" value={stats.coins} label="Xu" />
          <StatPill icon="honey" value={stats.streakCurrent} label="Chuỗi ngày học" />
          <SoundToggle />
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <nav className={styles.bottomNav} aria-label="Điều hướng">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) => `${styles.bottomLink} ${isActive ? styles.active : ""}`}
          >
            <span aria-hidden="true" className={styles.navIcon}>
              <Icon name={n.icon} size={28} />
            </span>
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
